/**
 * Новый пароль по ссылке из письма.
 *
 * Ключевая деталь, которую легко потерять: **переход по ссылке токен не
 * расходует.** Корпоративные почтовые сканеры (Microsoft Defender Safe Links,
 * Proofpoint) ходят по ссылкам до человека, и гашение при открытии ломало бы
 * восстановление у части клиентов молча и невоспроизводимо на нашей стороне.
 * Поэтому открытие только проверяет токен (`/auth/password/reset/validate`), а
 * гасит его отправка нового пароля.
 *
 * Отсюда же замаскированный адрес на экране: человек должен убедиться, что
 * меняет пароль тому аккаунту, которому собирался.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AuthCard } from './AuthCard';
import { Field } from '../../ui/form';
import { Empty, ErrorNotice, Skeleton } from '../../ui/state';
import {
  confirmPasswordReset,
  validatePasswordReset,
  type PasswordResetPreview,
  type Session,
} from '../../api/auth';
import { ApiError, fieldErrors } from '../../api/problem';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, shortPassword } from '../../ui/password';

/**
 * Три исхода проверки ссылки, а не два.
 *
 * Раньше было два: «годна» и «негодна с необязательной ошибкой». Экран рисовал
 * по одному поводу сразу ДВА состояния — красную полосу `ErrorNotice` («Ресурс
 * больше недоступен») и под ней `Empty` («Эта ссылка больше не работает»).
 * Человеку это читается как две разные беды.
 *
 * Разводятся не оформления, а случаи, и они действительно разные:
 *
 * * `spent` — сервер ответил, что ссылки нет: погашена или истекла (`410`).
 *   Повторять нечего, нужна новая ссылка;
 * * `unreachable` — ответа не было вовсе: сеть, край CDN, пятисотая. Ссылка при
 *   этом может быть жива, и «эта ссылка больше не работает» здесь просто ложь —
 *   человек пойдёт заказывать новую вместо того, чтобы повторить.
 */
type Check =
  | { kind: 'checking' }
  | { kind: 'valid'; preview: PasswordResetPreview }
  | { kind: 'spent' }
  | { kind: 'unreachable'; error: unknown };

export function Reset({ onDone }: { onDone: (session: Session) => void }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  // Ссылки без токена проверять не у кого — это видно сразу, без запроса.
  const [check, setCheck] = useState<Check>(() =>
    token ? { kind: 'checking' } : { kind: 'spent' },
  );
  // Счётчик попыток проверки. Кнопка «Повторить» обязана СХОДИТЬ на сервер
  // заново, а адрес ссылки при этом не меняется — без отдельной зависимости
  // эффект не перезапустился бы, и кнопка вешала бы вечное «Проверяем ссылку».
  const [attempt, setAttempt] = useState(0);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) return;
    let alive = true;
    validatePasswordReset(token)
      .then((preview) => {
        if (!alive) return;
        setCheck(preview.valid ? { kind: 'valid', preview } : { kind: 'spent' });
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        // `410` — единственный ответ, которым сервер объявляет ссылку мёртвой:
        // истёкшая и уже использованная неразличимы намеренно (подбирать по
        // ответу, какая из ссылок была настоящей, не должно получаться).
        // Всё остальное — не приговор ссылке, а неудача проверки.
        const spent = caught instanceof ApiError && caught.problem.status === 410;
        setCheck(spent ? { kind: 'spent' } : { kind: 'unreachable', error: caught });
      });
    return () => {
      alive = false;
    };
  }, [token, attempt]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      onDone(await confirmPasswordReset(token, password));
    } catch (caught) {
      if (caught instanceof ApiError) setFields(fieldErrors(caught.problem));
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (check.kind === 'checking') {
    return (
      <AuthCard title="Проверяем ссылку">
        <Skeleton rows={1} />
      </AuthCard>
    );
  }

  // Проверить не удалось. Ссылка, возможно, жива — предлагаем повторить, а не
  // объявляем её мёртвой и не гоним заказывать новую.
  if (check.kind === 'unreachable') {
    return (
      <AuthCard title="Не удалось проверить ссылку">
        <ErrorNotice
          error={check.error}
          action={
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => {
                setCheck({ kind: 'checking' });
                setAttempt((made) => made + 1);
              }}
            >
              Повторить
            </button>
          }
        />
      </AuthCard>
    );
  }

  // Ссылка погашена или истекла. Ровно одно оформленное состояние: сообщение и
  // выход из него. Красной полосы над ним нет — беда одна, и говорить о ней
  // дважды значит показывать две.
  if (check.kind === 'spent') {
    return (
      <AuthCard title="Ссылка не действует">
        <Empty
          eyebrow="Срок вышел"
          title="Эта ссылка больше не работает"
          action={
            <Link className="btn btn-primary" to="/auth/forgot">
              Прислать новую
            </Link>
          }
        >
          Ссылки для смены пароля живут сутки и гаснут после первого использования.
          Запросите новую — это займёт минуту.
        </Empty>
      </AuthCard>
    );
  }

  const short = shortPassword(password);

  return (
    <AuthCard
      title="Новый пароль"
      lead={<>Меняем пароль для {check.preview.email_masked}.</>}
      footer={
        <p className="micro">
          Все остальные устройства выйдут из кабинета — это нормально и так задумано.
        </p>
      }
    >
      {error ? <ErrorNotice error={error} /> : null}

      <form onSubmit={submit} noValidate>
        <Field
          id="password"
          label="Пароль"
          hint={PASSWORD_HINT}
          error={fields.password ?? short}
        >
          {(props) => (
            <input
              {...props}
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || password.length < PASSWORD_MIN_LENGTH}
        >
          {busy ? 'Сохраняем…' : 'Задать пароль и войти'}
        </button>
      </form>
    </AuthCard>
  );
}
